import { Badge } from "@/components/ui/Badge";
import { getStatusBadgeVariant, getStatusLabel } from "@/lib/constants";
import * as shared from "@/styles/shared";
import type { PaymentLogCardProps } from "@/types/ui";
import { translateLogMessage } from "@platko/shared/utils";
import { PaymentStatus } from "@shared/types/payment";
import { AlertTriangle, Check, Clock, ListChecks, Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EntryRow } from "./EntryRow";
import * as styles from "./PaymentLogCard.styles";

const PaymentLogCard = ({ log }: PaymentLogCardProps) => {
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation("payment");
  const { t: tBackend } = useTranslation("backendLogs");

  return (
    <>
      <button type="button" onClick={() => setShowModal(true)} className={styles.card}>
        <div className={styles.headerRow}>
          <div className={shared.rowGap2}>
            {getStatusIcon(log.status)}
            <span className={styles.statusTimeText}>
              <Clock className={styles.iconSm} />
              {new Date(log.runAt).toLocaleString()}
            </span>
          </div>
          <div className={shared.rowGap2}>
            {log.simulated && <span className={styles.simulatedBadge}>{t("simulated")}</span>}
            <Badge variant={getStatusBadgeVariant(log.status)} size="sm">
              {getStatusLabel(log.status)}
            </Badge>
            <span className={shared.mutedXs}>
              {log.entries.length} / {log.totalInvoices} {t("invoices")}
            </span>
          </div>
        </div>

        {log.errorMessage && <p className={styles.errorText}>{tBackend(log.errorMessage)}</p>}

        <div className={styles.entriesList}>
          {log.entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </div>

        <div className={styles.clickHint}>
          <ListChecks className={styles.clickHintIcon} />
          <span>{t("clickToViewLogs")}</span>
        </div>
      </button>

      {showModal && (
        <div className={styles.overlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={shared.rowGap2}>
                {getStatusIcon(log.status)}
                <h3 className={styles.modalTitle}>{t("logDetails")}</h3>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className={styles.closeBtn}>
                <X className={shared.iconSm} />
              </button>
            </div>

            <div className={styles.modalMeta}>
              <span className={shared.rowGap1}>
                <Clock className={styles.iconSm} />
                {new Date(log.runAt).toLocaleString()}
              </span>
              <span className={shared.rowGap1}>
                {log.entries.length} / {log.totalInvoices} {t("invoices")}
              </span>
              {log.simulated && <span className={styles.simulatedBadge}>{t("simulated")}</span>}
              <Badge variant={getStatusBadgeVariant(log.status)} size="sm">
                {getStatusLabel(log.status)}
              </Badge>
            </div>

            {log.errorMessage && <p className={styles.modalError}>{tBackend(log.errorMessage)}</p>}

            <div className={styles.modalSection}>
              <h4 className={styles.modalSectionTitle}>{t("logMessages")}</h4>
              <div className={styles.messagesList}>
                {!log.messages.length ? (
                  <p className={styles.noMessages}>{t("noMessages")}</p>
                ) : (
                  log.messages.map((msg) => (
                    <div key={msg.id} className={styles.messageItem}>
                      {translateLogMessage(tBackend, msg)}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className={styles.modalSection}>
              <h4 className={styles.modalSectionTitle}>{t("logEntries")}</h4>
              <div className={styles.entriesList}>
                {log.entries.map((entry) => (
                  <EntryRow key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const getStatusIcon = (status: PaymentStatus) => {
  if (status === PaymentStatus.COMPLETED) return <Check className={styles.checkIcon} />;
  if (status === PaymentStatus.FAILED) return <AlertTriangle className={styles.alertIcon} />;
  if (status === PaymentStatus.CANCELLED) return <X className={shared.iconSm} />;
  return <Loader2 className={styles.loaderIcon} />;
};

export { PaymentLogCard, getStatusIcon, type PaymentLogCardProps };
export default PaymentLogCard;
